import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Typography,
    message,
    Space,
    Button,
    Input,
    Tag as AntTag,
    Modal,
    Form,
    Popconfirm,
    Tooltip,
    Collapse
} from 'antd';
import {
    TagsOutlined,
    ReloadOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    GlobalOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { ApiService } from '../services/api';
import { Tag } from '../types';

const { Title } = Typography;
const { Search } = Input;
const { Panel } = Collapse;

const TagManagement: React.FC = () => {
    const [tags, setTags] = useState<Tag[]>([]);
    const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editingTag, setEditingTag] = useState<Tag | null>(null);
    const [editForm] = Form.useForm();
    const [createForm] = Form.useForm();

    useEffect(() => {
        loadTags();
    }, []);

    useEffect(() => {
        // 过滤标签
        if (searchText) {
            const filtered = tags.filter(tag =>
                tag.tag.toLowerCase().includes(searchText.toLowerCase()) ||
                tag.remark.toLowerCase().includes(searchText.toLowerCase()) ||
                tag.l10n_zh.toLowerCase().includes(searchText.toLowerCase()) ||
                tag.l10n_en.toLowerCase().includes(searchText.toLowerCase()) ||
                tag.l10n_he.toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredTags(filtered);
        } else {
            setFilteredTags(tags);
        }
    }, [tags, searchText]);

    const loadTags = async () => {
        setLoading(true);
        try {
            const response = await ApiService.getTagList({ language: 'zh' });
            if (response.code === 200) {
                setTags(response.data.tags);
            } else {
                message.error(response.message || '获取标签列表失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '获取标签列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (tag: Tag) => {
        setEditingTag(tag);
        editForm.setFieldsValue({
            tag: tag.tag,
            remark: tag.remark,
            l10n_zh: tag.l10n_zh,
            l10n_en: tag.l10n_en,
            l10n_zh_tw: tag.l10n_zh_tw,
            l10n_ja: tag.l10n_ja,
            l10n_ko: tag.l10n_ko,
            l10n_ar: tag.l10n_ar,
            l10n_de: tag.l10n_de,
            l10n_es: tag.l10n_es,
            l10n_es_mx: tag.l10n_es_mx,
            l10n_fr: tag.l10n_fr,
            l10n_he: tag.l10n_he,
            l10n_it: tag.l10n_it,
            l10n_ne: tag.l10n_ne,
            l10n_nl: tag.l10n_nl,
            l10n_pt: tag.l10n_pt,
            l10n_ru: tag.l10n_ru,
            l10n_th: tag.l10n_th,
            l10n_tr: tag.l10n_tr,
            l10n_vi: tag.l10n_vi
        });
        setEditModalOpen(true);
    };

    const handleDelete = async (tagId: number) => {
        try {
            const response = await ApiService.deleteTag({ id: tagId });
            if (response.code === 200) {
                message.success('标签删除成功');
                loadTags(); // 重新加载标签列表
            } else {
                message.error(response.message || '标签删除失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '标签删除失败');
        }
    };

    const handleSave = async () => {
        if (!editingTag) return;

        try {
            const values = await editForm.validateFields();
            const response = await ApiService.updateTag({
                id: editingTag.id,
                ...values
            });

            if (response.code === 200) {
                message.success('标签更新成功');
                setEditModalOpen(false);
                setEditingTag(null);
                editForm.resetFields();
                loadTags(); // 重新加载标签列表
            } else {
                message.error(response.message || '标签更新失败');
            }
        } catch (error: any) {
            if (error.errorFields) {
                message.error('请检查输入信息');
            } else {
                message.error(error.response?.data?.message || '标签更新失败');
            }
        }
    };

    const handleCancel = () => {
        setEditModalOpen(false);
        setEditingTag(null);
        editForm.resetFields();
    };

    const handleCreate = () => {
        setCreateModalOpen(true);
        createForm.resetFields();
    };

    const handleCreateSave = async () => {
        try {
            const values = await createForm.validateFields();
            const response = await ApiService.createTag(values);

            if (response.code === 200) {
                message.success('标签创建成功');
                setCreateModalOpen(false);
                createForm.resetFields();
                loadTags(); // 重新加载标签列表
            } else {
                message.error(response.message || '标签创建失败');
            }
        } catch (error: any) {
            if (error.errorFields) {
                message.error('请检查输入信息');
            } else {
                message.error(error.response?.data?.message || '标签创建失败');
            }
        }
    };

    const handleCreateCancel = () => {
        setCreateModalOpen(false);
        createForm.resetFields();
    };

    // 渲染多语言信息的展开内容
    const renderL10nInfo = (tag: Tag) => {
        const languages = [
            { key: 'l10n_zh', label: '中文(简体)', value: tag.l10n_zh },
            { key: 'l10n_zh_tw', label: '中文(繁体)', value: tag.l10n_zh_tw },
            { key: 'l10n_en', label: 'English(英语)', value: tag.l10n_en },
            { key: 'l10n_ja', label: '日本語(日语)', value: tag.l10n_ja },
            { key: 'l10n_ko', label: '한국어(韩语)', value: tag.l10n_ko },
            { key: 'l10n_ar', label: 'العربية(阿拉伯语)', value: tag.l10n_ar },
            { key: 'l10n_de', label: 'Deutsch(德语)', value: tag.l10n_de },
            { key: 'l10n_es', label: 'Español(西班牙语)', value: tag.l10n_es },
            { key: 'l10n_es_mx', label: 'Español (México)(墨西哥西班牙语)', value: tag.l10n_es_mx },
            { key: 'l10n_fr', label: 'Français(法语)', value: tag.l10n_fr },
            { key: 'l10n_he', label: 'עברית(希伯来语)', value: tag.l10n_he },
            { key: 'l10n_it', label: 'Italiano(意大利语)', value: tag.l10n_it },
            { key: 'l10n_ne', label: 'नेपाली(尼泊尔语)', value: tag.l10n_ne },
            { key: 'l10n_nl', label: 'Nederlands(荷兰语)', value: tag.l10n_nl },
            { key: 'l10n_pt', label: 'Português(葡萄牙语)', value: tag.l10n_pt },
            { key: 'l10n_ru', label: 'Русский(俄语)', value: tag.l10n_ru },
            { key: 'l10n_th', label: 'ไทย(泰语)', value: tag.l10n_th },
            { key: 'l10n_tr', label: 'Türkçe(土耳其语)', value: tag.l10n_tr },
            { key: 'l10n_vi', label: 'Tiếng Việt(越南语)', value: tag.l10n_vi }
        ];

        return (
            <div style={{ marginTop: 8 }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {languages.map(lang => (
                        <div key={lang.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 500, minWidth: 120 }}>{lang.label}:</span>
                            <span style={{ color: lang.value ? '#000' : '#999' }}>
                                {lang.value || '(未设置)'}
                            </span>
                        </div>
                    ))}
                </Space>
            </div>
        );
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            sorter: (a: Tag, b: Tag) => a.id - b.id,
        },
        {
            title: '标签名称',
            dataIndex: 'tag',
            key: 'tag',
            width: 150,
            render: (tag: string) => (
                <AntTag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    {tag}
                </AntTag>
            ),
        },
        {
            title: '标签描述',
            dataIndex: 'remark',
            key: 'remark',
            width: 200,
        },
        {
            title: '中文翻译',
            dataIndex: 'l10n_zh',
            key: 'l10n_zh',
            width: 120,
            render: (text: string) => text || <span style={{ color: '#999' }}>未设置</span>
        },
        {
            title: '英文翻译',
            dataIndex: 'l10n_en',
            key: 'l10n_en',
            width: 120,
            render: (text: string) => text || <span style={{ color: '#999' }}>未设置</span>
        },
        {
            title: '多语言信息',
            key: 'l10n_info',
            width: 120,
            render: (_: any, record: Tag) => (
                <Tooltip title="点击查看所有语言翻译">
                    <Button
                        type="text"
                        icon={<GlobalOutlined />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            Modal.info({
                                title: `标签 "${record.tag}" 的多语言信息`,
                                content: renderL10nInfo(record),
                                width: 600,
                                okText: '关闭'
                            });
                        }}
                    >
                        查看详情
                    </Button>
                </Tooltip>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 180,
            render: (_: any, record: Tag) => (
                <Space size="middle">
                    <Button
                        size="small"
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除"
                        description="删除标签后，已关联的图片将失去该标签。确定要删除吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确认"
                        cancelText="取消"
                        icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                    >
                        <Button
                            size="small"
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={2}>
                <TagsOutlined /> 标签管理
            </Title>

            <Card>
                <div style={{ marginBottom: 16 }}>
                    <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                        <Search
                            placeholder="搜索标签名称、描述或翻译"
                            allowClear
                            style={{ width: 350 }}
                            onChange={(e) => setSearchText(e.target.value)}
                            prefix={<SearchOutlined />}
                        />
                        <Space>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={loadTags}
                                loading={loading}
                            >
                                刷新
                            </Button>
                            <Button type="primary" onClick={handleCreate}>
                                添加标签
                            </Button>
                        </Space>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredTags}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        total: filteredTags.length,
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `第 ${range[0]}-${range[1]} 条，共 ${total} 条标签`,
                    }}
                    size="middle"
                    scroll={{ x: 1200 }}
                />

                <div style={{ marginTop: 16, color: '#666' }}>
                    <Space direction="vertical">
                        <div>💡 提示：</div>
                        <div>• 标签用于对图片进行分类管理</div>
                        <div>• 上传图片时需要指定对应的标签</div>
                        <div>• 可以通过标签筛选查看对应的图片</div>
                        <div>• 点击"多语言信息"可查看标签的所有语言翻译</div>
                    </Space>
                </div>
            </Card>

            {/* 编辑标签Modal */}
            <Modal
                title={`编辑标签：${editingTag?.tag || ''}`}
                open={editModalOpen}
                onOk={handleSave}
                onCancel={handleCancel}
                width={800}
                okText="保存"
                cancelText="取消"
            >
                <Form
                    form={editForm}
                    layout="vertical"
                >
                    <Form.Item
                        name="tag"
                        label="标签名称"
                        rules={[{ required: true, message: '请输入标签名称' }]}
                    >
                        <Input placeholder="请输入标签名称" />
                    </Form.Item>

                    <Form.Item
                        name="remark"
                        label="标签描述"
                        rules={[{ required: true, message: '请输入标签描述' }]}
                    >
                        <Input placeholder="请输入标签描述" />
                    </Form.Item>

                    <Collapse>
                        <Panel header="多语言翻译设置" key="1">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: '16px' }}>
                                <Form.Item name="l10n_zh" label="中文(简体)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="中文翻译" />
                                </Form.Item>
                                <Form.Item name="l10n_zh_tw" label="中文(繁体)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="繁体中文翻译" />
                                </Form.Item>
                                <Form.Item name="l10n_en" label="English(英语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="English translation" />
                                </Form.Item>
                                <Form.Item name="l10n_ja" label="日本語(日语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="日本語翻訳" />
                                </Form.Item>
                                <Form.Item name="l10n_ko" label="한국어(韩语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="한국어 번역" />
                                </Form.Item>
                                <Form.Item name="l10n_ar" label="العربية(阿拉伯语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="الترجمة العربية" />
                                </Form.Item>
                                <Form.Item name="l10n_de" label="Deutsch(德语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Deutsche Übersetzung" />
                                </Form.Item>
                                <Form.Item name="l10n_es" label="Español(西班牙语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traducción en español" />
                                </Form.Item>
                                <Form.Item name="l10n_es_mx" label="México-Español(墨西哥语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traducción en español mexicano" />
                                </Form.Item>
                                <Form.Item name="l10n_fr" label="Français(法语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traduction française" />
                                </Form.Item>
                                <Form.Item name="l10n_he" label="עברית(希伯来语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="תרגום עברי" />
                                </Form.Item>
                                <Form.Item name="l10n_it" label="Italiano(意大利语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traduzione italiana" />
                                </Form.Item>
                                <Form.Item name="l10n_ne" label="नेपाली(尼泊尔语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="नेपाली अनुवाद" />
                                </Form.Item>
                                <Form.Item name="l10n_nl" label="Nederlands(荷兰语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Nederlandse vertaling" />
                                </Form.Item>
                                <Form.Item name="l10n_pt" label="Português(葡萄牙语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Tradução em português" />
                                </Form.Item>
                                <Form.Item name="l10n_ru" label="Русский(俄语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Русский перевод" />
                                </Form.Item>
                                <Form.Item name="l10n_th" label="ไทย(泰语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="คำแปลภาษาไทย" />
                                </Form.Item>
                                <Form.Item name="l10n_tr" label="Türkçe(土耳其语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Türkçe çeviri" />
                                </Form.Item>
                                <Form.Item name="l10n_vi" label="Tiếng Việt(越南语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Bản dịch tiếng Việt" />
                                </Form.Item>
                            </div>
                        </Panel>
                    </Collapse>
                </Form>
            </Modal>

            {/* 创建标签Modal */}
            <Modal
                title="创建新标签"
                open={createModalOpen}
                onOk={handleCreateSave}
                onCancel={handleCreateCancel}
                width={800}
                okText="创建"
                cancelText="取消"
            >
                <Form
                    form={createForm}
                    layout="vertical"
                >
                    <Form.Item
                        name="tag"
                        label="标签名称"
                        rules={[{ required: true, message: '请输入标签名称' }]}
                    >
                        <Input placeholder="请输入标签名称" />
                    </Form.Item>

                    <Form.Item
                        name="remark"
                        label="标签描述"
                        rules={[{ required: true, message: '请输入标签描述' }]}
                    >
                        <Input placeholder="请输入标签描述" />
                    </Form.Item>

                    <Collapse>
                        <Panel header="多语言翻译设置" key="1">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: '16px' }}>
                                <Form.Item name="l10n_zh" label="中文(简体)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="中文翻译" />
                                </Form.Item>
                                <Form.Item name="l10n_zh_tw" label="中文(繁体)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="繁体中文翻译" />
                                </Form.Item>
                                <Form.Item name="l10n_en" label="English(英语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="English translation" />
                                </Form.Item>
                                <Form.Item name="l10n_ja" label="日本語(日语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="日本語翻訳" />
                                </Form.Item>
                                <Form.Item name="l10n_ko" label="한국어(韩语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="한국어 번역" />
                                </Form.Item>
                                <Form.Item name="l10n_ar" label="العربية(阿拉伯语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="الترجمة العربية" />
                                </Form.Item>
                                <Form.Item name="l10n_de" label="Deutsch(德语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Deutsche Übersetzung" />
                                </Form.Item>
                                <Form.Item name="l10n_es" label="Español(西班牙语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traducción en español" />
                                </Form.Item>
                                <Form.Item name="l10n_es_mx" label="México-Español(墨西哥语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traducción en español mexicano" />
                                </Form.Item>
                                <Form.Item name="l10n_fr" label="Français(法语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traduction française" />
                                </Form.Item>
                                <Form.Item name="l10n_he" label="עברית(希伯来语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="תרגום עברי" />
                                </Form.Item>
                                <Form.Item name="l10n_it" label="Italiano(意大利语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Traduzione italiana" />
                                </Form.Item>
                                <Form.Item name="l10n_ne" label="नेपाली(尼泊尔语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="नेपाली अनुवाद" />
                                </Form.Item>
                                <Form.Item name="l10n_nl" label="Nederlands(荷兰语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Nederlandse vertaling" />
                                </Form.Item>
                                <Form.Item name="l10n_pt" label="Português(葡萄牙语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Tradução em português" />
                                </Form.Item>
                                <Form.Item name="l10n_ru" label="Русский(俄语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Русский перевод" />
                                </Form.Item>
                                <Form.Item name="l10n_th" label="ไทย(泰语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="คำแปลภาษาไทย" />
                                </Form.Item>
                                <Form.Item name="l10n_tr" label="Türkçe(土耳其语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Türkçe çeviri" />
                                </Form.Item>
                                <Form.Item name="l10n_vi" label="Tiếng Việt(越南语)" style={{ marginBottom: '10px' }}>
                                    <Input placeholder="Bản dịch tiếng Việt" />
                                </Form.Item>
                            </div>
                        </Panel>
                    </Collapse>
                </Form>
            </Modal>
        </div>
    );
};

export default TagManagement; 